import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';

const UnifiedLoginPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { login } = useAuth();

  // Extract userType from pathname
  const pathname = location.pathname;
  let userType = 'merchant'; // default
  if (pathname.includes('/admin/login')) {
    userType = 'admin';
  } else if (pathname.includes('/merchant/login')) {
    userType = 'merchant';
  } else if (pathname.includes('/customer/login')) {
    userType = 'customer';
  }

  const [formData, setFormData] = useState({
    username: '',
    password: ''
  });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  // Configuration for different user types
  const userTypeConfig = {
    admin: {
      title: 'Admin Login',
      subtitle: 'Access the admin dashboard',
      icon: '🔐',
      primaryColor: 'indigo',
      placeholder: {
        username: 'Admin Username',
        password: 'Admin Password'
      }
    },
    merchant: {
      title: 'Merchant Login',
      subtitle: 'Manage your business and customers',
      icon: '🏪',
      primaryColor: 'blue',
      placeholder: {
        username: 'Merchant Username or Email',
        password: 'Password'
      }
    },
    customer: {
      title: 'Customer Login',
      subtitle: 'View your account and payments',
      icon: '👤',
      primaryColor: 'green',
      placeholder: {
        username: 'Customer Username or Mobile',
        password: 'Password'
      }
    }
  };

  const config = userTypeConfig[userType] || userTypeConfig.merchant;

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
    setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      await login(formData.username, formData.password, userType);

      // Redirect based on user type
      const redirectMap = {
        admin: '/admin/dashboard',
        merchant: '/dashboard',
        customer: '/customer/dashboard'
      };

      const from = location.state?.from?.pathname || redirectMap[userType];
      navigate(from, { replace: true });
    } catch (err) {
      // Note: axios interceptor already extracted response.data, so err is the data object
      setError(err.message || 'Invalid credentials. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const colorClasses = {
    indigo: {
      bg: 'bg-indigo-600 hover:bg-indigo-700',
      text: 'text-indigo-600',
      border: 'border-indigo-500',
      focus: 'focus:ring-indigo-500'
    },
    blue: {
      bg: 'bg-blue-600 hover:bg-blue-700',
      text: 'text-blue-600',
      border: 'border-blue-500',
      focus: 'focus:ring-blue-500'
    },
    green: {
      bg: 'bg-green-600 hover:bg-green-700',
      text: 'text-green-600',
      border: 'border-green-500',
      focus: 'focus:ring-green-500'
    }
  };

  const colors = colorClasses[config.primaryColor];

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        {/* Logo/Header */}
        <div className="text-center">
          <h2 className="text-3xl font-bold text-gray-900">
            {config.title}
          </h2>
          <p className="mt-2 text-sm text-gray-600">
            {config.subtitle}
          </p>
        </div>

        {/* Login Form */}
        <div className="bg-white rounded-lg shadow-lg p-8">
          <form className="space-y-6" onSubmit={handleSubmit}>
            {error && (
              <div className="bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded-md text-sm">
                {error}
              </div>
            )}

            <div>
              <label htmlFor="username" className="block text-sm font-medium text-gray-700 mb-2">
                {config.placeholder.username}
              </label>
              <input
                id="username"
                name="username"
                type="text"
                autoComplete="username"
                required
                value={formData.username}
                onChange={handleChange}
                className={`appearance-none relative block w-full px-3 py-3 border border-gray-300 rounded-md placeholder-gray-400 text-gray-900 focus:outline-none focus:ring-2 ${colors.focus} focus:border-transparent transition-all`}
                placeholder={config.placeholder.username}
              />
            </div>

            <div>
              <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-2">
                Password
              </label>
              <input
                id="password"
                name="password"
                type="password"
                autoComplete="current-password"
                required
                value={formData.password}
                onChange={handleChange}
                className={`appearance-none relative block w-full px-3 py-3 border border-gray-300 rounded-md placeholder-gray-400 text-gray-900 focus:outline-none focus:ring-2 ${colors.focus} focus:border-transparent transition-all`}
                placeholder="Enter your password"
              />
            </div>

            {userType === 'merchant' && (
              <div className="flex items-center justify-between text-sm">
                <a href="/forgot-password" className={`font-medium ${colors.text} hover:opacity-80`}>
                  Forgot password?
                </a>
                <a href="/register" className={`font-medium ${colors.text} hover:opacity-80`}>
                  Register
                </a>
              </div>
            )}

            <button
              type="submit"
              disabled={loading}
              className={`w-full flex justify-center py-3 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white ${colors.bg} focus:outline-none focus:ring-2 focus:ring-offset-2 ${colors.focus} transition-colors disabled:opacity-50 disabled:cursor-not-allowed`}
            >
              {loading ? 'Signing in...' : 'Sign in'}
            </button>
          </form>

          {/* Additional Info */}
          {userType === 'admin' && (
            <div className="mt-4 text-center text-xs text-gray-500">
              Admin access only. Unauthorized access is prohibited.
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="text-center text-sm text-gray-500">
          <p>© 2025 Bahbeta (BB Books). All rights reserved.</p>
        </div>
      </div>
    </div>
  );
};

export default UnifiedLoginPage;
